import Image from 'next/image'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { BlockCall } from 'types/block-call'

type Props = {
  data: Array<BlockCall>
}
// pegar largura da tela para responsividade
const getItemsPerPage = () => {
  if (typeof window !== 'undefined') {
    const width = window.innerWidth
    if (width < 600) return 1
    return 2
  }
  return 2
}

const AnimatedCarouselBlockCalls = ({ data }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(2) // Sempre inicia com 2 para consistência SSR
  const [isClient, setIsClient] = useState(false)

  const totalPages = useMemo(
    () => Math.ceil(data.length / itemsPerPage),
    [data.length, itemsPerPage]
  )

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % totalPages)
  }, [totalPages])

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  // Marcar que estamos no cliente e ajustar itemsPerPage após hidratação
  useEffect(() => {
    setIsClient(true)
    setItemsPerPage(getItemsPerPage())
  }, [])

  useEffect(() => {
    if (!isClient) return

    const handleResize = () => {
      const newItemsPerPage = getItemsPerPage()
      setItemsPerPage(newItemsPerPage)
      // Reset para primeira página se o índice atual estiver fora do alcance
      setCurrentIndex(prev => {
        const newTotalPages = Math.ceil(data.length / newItemsPerPage)
        return prev >= newTotalPages ? 0 : prev
      })
    }

    window.addEventListener('resize', handleResize)

    const interval = setInterval(() => {
      nextSlide()
    }, 600000) // 10 minutos

    return () => {
      clearInterval(interval)
      window.removeEventListener('resize', handleResize)
    }
  }, [data.length, nextSlide, isClient])

  const getCurrentItems = () => {
    const startIndex = currentIndex * itemsPerPage
    return data.slice(startIndex, startIndex + itemsPerPage)
  }

  return (
    <div className="relative w-full">
      {/* Container dos itens */}
      <div className="flex justify-center gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {getCurrentItems().map((item, index) => (
          <div
            key={`${currentIndex}-${index}`}
            className="relative w-full sm:w-1/2 h-[200px] sm:h-[240px] md:h-[280px] rounded-xl shadow-lg"
          >
            <Image
              alt={item.imageAlt}
              src={item.imageUrl}
              fill
              className="object-cover transition-transform duration-300 rounded-xl"
              loading="lazy"
            />
            {/* Overlay com informações */}
            <div className="flex flex-col w-full sm:w-1/2 items-start justify-center absolute bottom-0 h-full left-0 bg-gradient-to-r from-black/60 to-transparent rounded-xl p-3 sm:p-4 text-white pl-4 sm:pl-6 md:pl-12">
              <h3 className="font-bold text-xl sm:text-2xl md:text-4xl mb-1 sm:mb-2 text-right">
                {item.title || ''}
              </h3>
              <span className="w-12 sm:w-16 bg-[#7be500] h-1"></span>
              <p className="py-1 sm:py-2 text-sm sm:text-base md:text-xl font-medium opacity-90">
                {item.description || ''}
              </p>
              <a href={item.buttonLink || '#'}>
                <button className="mt-1 sm:mt-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#63F14B] text-black rounded-full flex justify-center items-center text-xs sm:text-sm">
                  <span className="pt-[2px] pe-1 sm:pe-2">►</span>{' '}
                  {item.buttonText || 'Veja a Prévia'}
                </button>
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Indicadores de paginação (bolinhas) */}
      <div className="flex justify-center gap-1.5">
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors duration-200 ${
              index === currentIndex
                ? 'w-7 bg-gray-400'
                : 'hover:bg-gray-400 bg-gray-200 hover:w-7'
            }`}
            aria-label={`Ir para slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

export const BlockCallsCarousel = AnimatedCarouselBlockCalls
